import { motion } from "framer-motion";
import { useScrollAnimation } from "../../utils/useScrollAnimation";

function PricingFaq() {
  const faqItems = [
    {
      question: "How does the free plan work?",
      answer: "The free plan allows you to process up to 100 emails per month with our AI features. You'll get access to basic categorization, weekly summaries, and essential organizing tools. It's perfect for personal use or to get a feel for how MailMaid works before upgrading."
    },
    {
      question: "Can I change plans at any time?",
      answer: "Yes, you can upgrade, downgrade, or cancel your plan at any time. If you upgrade, the new features will be available immediately. If you downgrade, the changes will take effect at the start of your next billing cycle."
    },
    {
      question: "Do you offer discounts for annual billing?",
      answer: "Yes! You'll save 20% when you choose annual billing compared to the monthly price. The discount is automatically applied when you select annual billing during signup or when changing your plan."
    },
    {
      question: "Is there a limit to how many email accounts I can connect?",
      answer: "The Free plan allows you to connect 1 email account. The Pro plan allows up to 3 email accounts per user. The Enterprise plan offers unlimited email accounts, making it perfect for teams managing multiple mailboxes."
    },
    {
      question: "What happens if I exceed my email processing limit on the free plan?",
      answer: "If you exceed the 100 email processing limit on the free plan, you'll still be able to use basic features, but AI-powered features will be paused until the next month or until you upgrade to a paid plan."
    },
    {
      question: "How does the 14-day trial work?",
      answer: "Our 14-day trial gives you full access to all Pro features. No credit card is required to start. At the end of the trial, you can choose to subscribe to a paid plan or continue with the free plan. We also offer a money-back guarantee if you're not satisfied with your purchase."
    }
  ];

  return (
    <section className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-[#694e42]">Frequently Asked Questions</h2>
          <p className="text-lg text-[#8a7266] max-w-2xl mx-auto">
            Got questions about our pricing or plans? Find answers to the most common questions below.
          </p>
        </motion.div>
        
        <div className="space-y-6">
          {faqItems.map((faq, index) => {
            const { ref, isInView } = useScrollAnimation();
            
            return (
              <motion.div
                key={index}
                ref={ref}
                className="bg-[#f9f6f4] rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer list-none">
                    <h3 className="font-bold text-lg text-[#694e42]">{faq.question}</h3>
                    <svg
                      className="w-5 h-5 text-[#ff8c42] transition-transform duration-300 group-open:rotate-180"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </summary>
                  <div className="mt-4 text-[#8a7266]">
                    <p>{faq.answer}</p>
                  </div>
                </details>
              </motion.div>
            );
          })}
        </div>
        
        <motion.div
          className="text-center mt-12 p-8 bg-[#fff5eb] rounded-xl border border-[#e2dad3]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-xl font-bold text-[#694e42] mb-4">Still have questions?</h3>
          <p className="text-[#8a7266] mb-6">
            Contact our friendly support team and we'll be happy to help you find the right plan for your needs.
          </p>
          <motion.button
            className="px-6 py-3 bg-[#ff8c42] text-white rounded-md font-medium"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Contact Support
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}

export default PricingFaq;